/**
 * useStopModeration hook
 *
 * Encapsulates the "Stop Moderation" flow: tracks discarded labels,
 * stops the session, and navigates back to the dashboard.
 *
 * Cases do not need to be released — the backend automatically re-adds
 * them to the queue when the session expires.
 */

import { track } from '@core/logging';
import { useCallback } from 'react';

import type { ModerationCase } from '../domain/ModerationCase';

interface UseStopModerationParams {
  /** All session cases currently displayed */
  sessionCases: ModerationCase[];
  /** Case IDs with labels applied (for tracking discarded labels) */
  appliedCaseIds: string[];
  /** Dispatches STOP_SESSION to mark session inactive */
  stopSession: () => void;
  /** Callback to navigate back to dashboard */
  onReturnToDashboard: () => void;
}

export interface UseStopModerationReturn {
  /** Stop session and return to dashboard */
  handleStopModeration: () => void;
}

/**
 * Hook that manages the Stop Moderation flow (T050).
 * Any applied-but-not-submitted labels are auto-discarded per spec (T051).
 *
 * @returns Stop handler
 */
export function useStopModeration({ sessionCases, appliedCaseIds, stopSession, onReturnToDashboard }: UseStopModerationParams): UseStopModerationReturn {
  const handleStopModeration = useCallback(() => {
    track('moderation_stop', {
      sessionCaseCount: String(sessionCases.length),
      discardedLabelsCount: String(appliedCaseIds.length),
    });

    stopSession();
    onReturnToDashboard();
  }, [sessionCases, appliedCaseIds, stopSession, onReturnToDashboard]);

  return { handleStopModeration };
}
